import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Quote,
    Monitor,
    Users,
    Database,
    RefreshCw,
    Cloud,
    ShieldCheck,
    GraduationCap,
    HeartPulse,
    Landmark,
    Laptop,
    Server,
    HardDrive,
    CloudUpload,
    CloudDownload,
    GitBranch,
    AppWindow,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import {
    DOWNLOAD_HREF,
    TopBar,
    FooterBar,
    GradientOrbVisual,
} from "@/components/marketing/Chrome";

/**
 * UseCases — /use-cases. Sidebar-tabbed layout matching Documentation's
 * pattern: only "Business IT" has full content shown (matching the
 * reference), the other 8 categories are listed as a visual index rather
 * than tabs with fabricated content. The reference's "office/data center/
 * school/hospital/government" photography is replaced with the same
 * abstract gradient visual language used elsewhere -- see the earlier,
 * explicitly resolved decision not to use unlicensed stock photography.
 */
export default function UseCases() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="usecases-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <Hero />
            <BusinessIT />
            <MoreUseCases />
            <CommonScenarios />
            <FinalCTA />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function Hero() {
    return (
        <section className="relative z-10 overflow-hidden border-b border-white/5 px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="usecases-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[140px]" />
            <div className="relative mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
                <div>
                    <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">Use Cases</div>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                        Real-world migrations. Real business <span className="text-[#3fb8ff]">impact.</span>
                    </h1>
                    <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-white/65">
                        MoovBytes is designed for IT teams, MSPs, businesses, and infrastructure teams that need to move data reliably between PCs, servers, NAS systems, and cloud storage — with confidence.
                    </p>
                </div>
                <GradientOrbVisual heading={<>SAME DATA.<br />A BRIGHTER TOMORROW.</>} />
            </div>
        </section>
    );
}

const CATEGORIES = [
    { label: "Business IT", icon: Monitor, active: true },
    { label: "Managed Service Providers", icon: Users },
    { label: "Data Center & Infrastructure", icon: Database },
    { label: "Device Refresh", icon: RefreshCw },
    { label: "Cloud Migration", icon: Cloud },
    { label: "Disaster Recovery", icon: ShieldCheck },
    { label: "Education", icon: GraduationCap },
    { label: "Healthcare", icon: HeartPulse },
    { label: "Government", icon: Landmark },
];

function BusinessIT() {
    return (
        <section className="relative z-10 px-6 py-16 sm:px-6" data-testid="usecases-business-it">
            <div className="mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[280px_1fr]">
                <aside className="space-y-1 lg:sticky lg:top-24 lg:self-start">
                    {CATEGORIES.map((c) => (
                        <div
                            key={c.label}
                            className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13px] font-medium ${
                                c.active ? "bg-gradient-to-r from-[#00C2FF]/20 to-[#0066ff]/20 text-white" : "text-white/60"
                            }`}
                        >
                            <c.icon size={15} className={c.active ? "text-[#3fb8ff]" : "text-white/40"} />
                            {c.label}
                        </div>
                    ))}
                </aside>

                <div className="grid gap-8 rounded-2xl border border-white/10 bg-white/[0.03] p-8 lg:grid-cols-[1fr_0.9fr] lg:items-center">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Business IT</div>
                        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Keep your business moving.</h2>
                        <p className="mt-3 text-[13.5px] leading-relaxed text-white/60">
                            Migrate user data, file servers, and department storage with minimal downtime. MoovBytes helps IT teams move entire systems, user profiles, and critical data between PCs, servers, NAS systems, and cloud storage — quickly, securely, and with full verification.
                        </p>
                        <ul className="mt-5 space-y-2.5">
                            {["Migrate user profiles and data", "Support Windows 10/11 upgrades", "Minimize downtime", "Verify and report on migration results"].map((item) => (
                                <li key={item} className="flex items-start gap-2.5 text-[13px] text-white/75">
                                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#3fb8ff]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={DOWNLOAD_HREF}
                            className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-6 py-3 text-[13px] font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110"
                        >
                            <AppWindow size={15} />
                            Download for Windows
                        </Link>
                    </div>
                    <div className="relative">
                        <div className="pointer-events-none absolute -inset-6 -z-10 rounded-full bg-[#0066ff]/20 blur-[80px]" />
                        <div
                            className="aspect-[4/3] w-full rounded-2xl"
                            style={{ background: "radial-gradient(circle at 30% 25%, #3fb8ff, #0a4fd6 50%, #050e22 85%)" }}
                        />
                        <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-white/10 bg-[#050e22]/90 p-4 backdrop-blur">
                            <Quote size={16} className="text-[#3fb8ff]/70" />
                            <p className="mt-1.5 text-[12.5px] italic leading-relaxed text-white/80">
                                "MoovBytes made our department migration simple and stress-free. Everything just worked."
                            </p>
                            <div className="mt-2 text-[11px] text-white/45">IT Manager, Mid-Sized Business</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const MORE_USE_CASES = [
    { icon: Users, title: "Managed Service Providers", body: "Migrate client data across PCs, servers, and cloud environments. Standardize and automate migrations for multiple customers with reliable, repeatable results." },
    { icon: Database, title: "Data Center & Infrastructure", body: "Move data between physical servers, virtual machines, NAS systems, and cloud storage. Handle large-scale migrations with speed, integrity, and minimal disruption." },
    { icon: Laptop, title: "Device Refresh", body: "Migrate user data, settings, and applications to new Windows devices. Simplify PC refresh projects and ensure a seamless end-user experience." },
    { icon: Cloud, title: "Cloud Migration", body: "Move data to and from cloud platforms including Azure, AWS, and other S3-compatible storage. Bridge on-premises and cloud environments with ease." },
    { icon: ShieldCheck, title: "Disaster Recovery", body: "Quickly restore data to new hardware or alternate locations. MoovBytes helps you keep data accessible when it matters most." },
    { icon: GraduationCap, title: "Education", body: "Migrate data for schools, universities, and research institutions. Move user profiles, lab environments, and department data with minimal disruption." },
    { icon: HeartPulse, title: "Healthcare", body: "Securely migrate patient data, department shares, and user profiles while maintaining compliance requirements." },
    { icon: Landmark, title: "Government", body: "Support secure migrations for federal, state, and local organizations. Move data between PCs, servers, and approved cloud environments with confidence." },
];

function MoreUseCases() {
    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-20 sm:px-6" data-testid="usecases-more">
            <div className="mx-auto max-w-[1600px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">More Use Cases</div>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Built for every environment.</h2>
                <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
                    From small businesses to global enterprises, MoovBytes helps teams move data where it needs to go.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {MORE_USE_CASES.map((c) => (
                        <div key={c.title} className="group rounded-xl border border-white/10 bg-white/[0.03] p-5 transition hover:border-[#00C2FF]/30 hover:bg-white/[0.045]">
                            <div className="flex items-center justify-between">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#00C2FF]/30 bg-[#00C2FF]/10 text-[#3fb8ff]">
                                    <c.icon size={17} />
                                </div>
                                <ArrowRight size={14} className="text-white/25 transition group-hover:translate-x-0.5 group-hover:text-[#3fb8ff]" />
                            </div>
                            <div className="mt-3 text-[13.5px] font-bold text-white">{c.title}</div>
                            <p className="mt-1.5 text-[12px] leading-relaxed text-white/55">{c.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CommonScenarios() {
    const SCENARIOS = [
        { icon: Laptop, from: "PC", to: "PC", body: "Move user data, settings, and applications." },
        { icon: Server, from: "Server", to: "Server", body: "Migrate between Windows servers with full data integrity." },
        { icon: HardDrive, from: "NAS", to: "NAS", body: "Transfer data between SMB shares and NAS devices." },
        { icon: CloudUpload, from: "On-Prem", to: "Cloud", body: "Move data to Azure, AWS, and other cloud platforms." },
        { icon: CloudDownload, from: "Cloud", to: "On-Prem", body: "Bring cloud data back to local environments." },
        { icon: GitBranch, from: "Cross-Environment", to: "", body: "Migrate between any combination of PCs, servers, NAS, and cloud." },
    ];
    return (
        <section className="relative z-10 border-t border-white/5 px-6 py-20 sm:px-6" data-testid="usecases-scenarios">
            <div className="mx-auto max-w-[1600px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Common Scenarios</div>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Real migrations. Real results.</h2>
                <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">MoovBytes handles a wide range of migration scenarios.</p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                    {SCENARIOS.map((s) => (
                        <div key={s.from} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                            <s.icon size={18} className="text-[#3fb8ff]" />
                            <div className="mt-2.5 text-[12.5px] font-bold text-white">{s.to ? `${s.from} to ${s.to}` : s.from}</div>
                            <p className="mt-1 text-[11px] leading-relaxed text-white/55">{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    return (
        <section className="relative z-10 px-6 pb-20 sm:px-6" data-testid="usecases-cta">
            <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#00C2FF]/10 via-white/[0.03] to-[#5b5ef0]/15 p-8 sm:flex-row sm:items-center">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Ready to power your next migration?</div>
                    <h2 className="mt-2 font-display text-xl font-bold tracking-tight sm:text-2xl">Download MoovBytes for Windows</h2>
                    <p className="mt-1.5 text-[13px] text-white/60">Join thousands of IT professionals moving data with confidence.</p>
                </div>
                <div className="shrink-0 text-center">
                    <Link
                        to={DOWNLOAD_HREF}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110"
                    >
                        <AppWindow size={16} />
                        Download for Windows
                    </Link>
                    <div className="mt-2 text-[11px] text-white/40">Windows 10/11 · Windows Server 2019–2025 · 64-bit</div>
                </div>
            </div>
        </section>
    );
}
